import { createContext, ReactNode, useContext, useReducer } from "react";
export type NotificationState = {
  actionAnchorElement: HTMLButtonElement | null;
  notifications: any;
  hasMore: boolean;
  nextCursor: any;
  hasNotificationUnread: boolean;
};

export type NotificationContextType = {
  state: NotificationState;
  fetchNotifications: (
    nextPageNotification: any[],
    hasMore: boolean,
    nextCursor?: string
  ) => void;
  toggleNotificationsPopup: (
    actionAnchorElement: HTMLButtonElement | null
  ) => void;
  updateHasNotificationUnread: (hasNotificationUnread: boolean) => void;
  addNotification: (newNotification: any) => void;
};

export type NotificationAction =
  | { type: "FETCH_NOTIFICATIONS"; payload: any }
  | { type: "UPDATE_HAS_NOTIFICATION_UNREAD"; payload: any }
  | { type: "ADD_NOTIFICATION"; payload: any }
  | { type: "TOGGLE_NOTIFICATION_POPUP"; payload: any };

export const NotificationsContext =
  createContext<NotificationContextType | null>(null);

const NotificationReducer = (
  state: NotificationState,
  action: NotificationAction
) => {
  switch (action.type) {
    case "FETCH_NOTIFICATIONS": {
      return {
        ...state,
        notifications: [
          ...state.notifications,
          ...action.payload.nextPageNotification,
        ],
        hasMore: action.payload.hasMore,
        nextCursor: action.payload.nextCursor,
      };
    }
    case "UPDATE_HAS_NOTIFICATION_UNREAD": {
      return {
        ...state,
        hasNotificationUnread: action.payload.hasNotificationUnread,
      };
    }
    case "ADD_NOTIFICATION": {
      return {
        ...state,
        notifications: [action.payload.newNotification, ...state.notifications],
      };
    }
    case "TOGGLE_NOTIFICATION_POPUP": {
      return {
        ...state,
        actionAnchorElement: action.payload.actionAnchorElement,
      };
    }

    default:
      return state;
  }
};

export const NotificationsProvider = (props: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(NotificationReducer, {
    actionAnchorElement: null,
    notifications: [],
    hasMore: true,
    nextCursor: null,
    hasNotificationUnread: false,
  });
  const { children } = props;

  const fetchNotifications = (
    nextPageNotification: any[],
    hasMore: boolean,
    nextCursor?: string | null
  ) => {
    dispatch({
      type: "FETCH_NOTIFICATIONS",
      payload: {
        nextPageNotification,
        hasMore,
        nextCursor,
      },
    });
  };

  const updateHasNotificationUnread = (hasNotificationUnread?: boolean) => {
    dispatch({
      type: "UPDATE_HAS_NOTIFICATION_UNREAD",
      payload: { hasNotificationUnread },
    });
  };

  const addNotification = (newNotification: any) => {
    dispatch({
      type: "ADD_NOTIFICATION",
      payload: { newNotification },
    });
  };

  const toggleNotificationsPopup = (
    actionAnchorElement: HTMLButtonElement | null
  ) => {
    dispatch({
      type: "TOGGLE_NOTIFICATION_POPUP",
      payload: { actionAnchorElement },
    });
  };

  return (
    <NotificationsContext.Provider
      value={{
        state,
        fetchNotifications,
        toggleNotificationsPopup,
        updateHasNotificationUnread,
        addNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("");
  }
  return context;
};
